import { all, call, getContext, put, select, spawn, take, takeEvery } from 'redux-saga/effects';

import { actions as globalActions, selectors as globalSelectors } from '@elab/core/store/global';
import dynaForms, { dtoBuilder } from '@elab/core/utils/dynaForms';
import dynaFormsApi from '@elab/core/utils/dynaForms/api';
import {
    actions as userActions,
    selectors as userSelectors,
    types as userTypes,
} from '@elab/core/store/user';
import { actions as uiActions, selectors as uiSelectors } from '@elab/core/store/ui';
import analytics from '@elab/core/utils/analytics';
import { selectors as configSelectors } from '@elab/core/store/config';
import {
    digitalCardAddedSuccessMessage,
    physicalCardAddedSuccessMessage,
    registrationPrefillFields,
} from '@elab/core/utils/constants';
import { defineAdditionalAddressFieldsDisplay } from '@elab/core/utils/address/sagaHelpers';
import { doFieldValidation } from '@elab/core/utils/validations/doFieldValidation';
import { getPrefillValue } from '@elab/core/utils/personalInfo';
import { runPinCodeChecks } from '@elab/components/connected/forms/PinCodeForm/redux/saga';

import api from './api';
import createActions from './actions';
import getSelectors from './selectors';
import getTypes from './types';

const physicalStore = 'physical-loyalty-card';
const digitalStore = 'digital-loyalty-card';

function* doSubmit(dto, { selectors }, submitUrl, dialogInfo = {}) {
    const setDataProtectionAccetpted = yield select(configSelectors.setDataProtectionAccetpted);
    if (
        setDataProtectionAccetpted &&
        dto &&
        dto.loyaltyEnrollment &&
        dto.loyaltyEnrollment.gdprServiceInformation
    ) {
        dto.loyaltyEnrollment.gdprServiceInformation.dataProtectionAccepted = true;
    }
    yield call(dynaFormsApi.doSubmit, submitUrl, dto);
    yield call(changePopupState, false);

    const pageViewType = yield select(uiSelectors.getPageViewType);
    if (pageViewType === 'physical') {
        yield spawn(analytics.handleLinkLoyaltyCard, 4);
    }

    yield put(uiActions.resetPageViewType());
    yield put(userActions.loadProfile());
    yield take(userTypes.SET_PROFILE);
    const delay = yield select(configSelectors.messageDelay);
    const loyaltyCardAddedSuccessMessage =
        pageViewType === 'physical'
            ? physicalCardAddedSuccessMessage
            : digitalCardAddedSuccessMessage;
    const message = {
        ...loyaltyCardAddedSuccessMessage,
        delay,
    };
    yield put(globalActions.addMessage(message));

    const successRedirectUrl = yield select(selectors.getSuccessRedirectLink);

    if (dialogInfo.iframeUrl) {
        dialogInfo.iframeRedirectOnClose = successRedirectUrl;
        yield put(uiActions.openDialog(dialogInfo));
    } else {
        yield put(globalActions.redirect(successRedirectUrl));
    }
}

function* watchPopUp({ selectors, types, storeName }) {
    yield takeEvery([types.SET_STEP, types.SET_STEP_BACK], function*() {
        const metadata = yield select(selectors.getMetadata);
        const data = yield select(selectors.getData);
        const hasError = Object.values(data).find(({ error }) => error);
        const currentStep = yield select(selectors.getStep);
        if (storeName === physicalStore) {
            yield spawn(analytics.handleLinkLoyaltyCard, currentStep === 2 ? 2 : 0);
        }

        const isCurrentStepPopup = metadata[currentStep - 1] && metadata[currentStep - 1].popUp;
        if (hasError) {
            return;
        }
        yield call(changePopupState, isCurrentStepPopup);
    });
}

function changePopupState(isPopup) {
    const mainEl = document.querySelector('.e2-main');
    const bodyEl = document.querySelector('body');
    const myAccFormEl = document.querySelector('.my-account--background-color');

    mainEl.style.position = isPopup ? 'relative' : '';
    if (isPopup) {
        bodyEl.setAttribute('prevent-scroll-for-popup', '');
    } else {
        bodyEl.removeAttribute('prevent-scroll-for-popup');
    }
    if (myAccFormEl) {
        myAccFormEl.style.visibility = isPopup ? 'hidden' : '';
    }
}

function* watchFormInitialize({ actions, types, storeName, selectors }) {
    yield takeEvery(types.SET_INITIAL_DATA, function*({ payload }) {
        const profile = yield select(userSelectors.getProfile);
        if (!Object.keys(profile).includes('uid')) {
            yield take(userTypes.SET_PROFILE);
        }
        if (storeName === physicalStore) {
            const step = yield select(selectors.getStep);
            yield spawn(analytics.handleLinkLoyaltyCard, step === 2 ? 2 : 0);
        }

        const isLoyaltyUser = yield select(userSelectors.isLoyalty);
        if (isLoyaltyUser) {
            const metadataFull = (payload && payload.metadata) || [];
            const metadataOneStep = metadataFull[0];
            yield put(actions.setMetadata([metadataOneStep]));
        }
    });
}

function* watchSubmitLoyaltyCard({ types, storeName }) {
    yield takeEvery(types.SUBMIT, function*() {
        const selectors = getSelectors(storeName);
        if (storeName === physicalStore) {
            const step = yield select(selectors.getStep);
            yield spawn(analytics.handleLinkLoyaltyCard, step === 2 ? 3 : 1);
            if (step === 2) {
                yield put(uiActions.setPageViewType('physical'));
            }
        }
    });
}

export const createFieldValueGetter = (data = {}) => (user = {}, fieldName) => {
    const value = getPrefillValue(user, fieldName);
    return value || (data[fieldName] || {}).value;
};

export function* initialize(ctx) {
    yield call(prefillFormFields, ctx);

    const country = yield select(globalSelectors.getCountry);
    const shouldValidateByAge = yield select(configSelectors.shouldValidateByAge, country);
    if (!shouldValidateByAge) {
        return;
    }
    yield call(doFieldValidation, 'dateOfBirth');
}

export function* prefillFormFields({ actions, selectors, storeName }) {
    const autofillForm = yield select(configSelectors.autofillLoyaltyCardForm);

    if (!autofillForm || storeName !== digitalStore) return;

    const user = yield select(userSelectors.getProfile);
    const data = yield select(selectors.getData);
    const fieldNames = Object.keys(data);
    const getFieldValue = yield call(createFieldValueGetter, data);

    yield call(defineAdditionalAddressFieldsDisplay, {
        storeName,
        valuesSource: user,
        extractValuesWith: getFieldValue,
    });

    yield all(
        fieldNames.map(fieldName =>
            put(actions.initDataFieldValue(fieldName, getFieldValue(user, fieldName))),
        ),
    );
}

export function* afterStepValidation({ storeName, actions, selectors }) {
    const step = yield select(selectors.getStep);
    if (step === 1 && storeName !== digitalStore) {
        const pinStatus = yield call(runPinCodeChecks);

        if (!pinStatus) {
            return false;
        }
    }

    const autofillForm = yield select(configSelectors.autofillLoyaltyCardForm);
    const canContinue = true;
    if (!autofillForm || storeName !== physicalStore) return canContinue;

    const metadata = yield select(selectors.getMetadata);
    const data = yield select(selectors.getData);
    if (metadata[step] && metadata[step].prefilled) {
        const DTO = yield call(dtoBuilder, data);
        const res = yield call(api.getPrefillInformation, DTO);

        if (!res) return canContinue;

        const getFieldValue = yield call(createFieldValueGetter);
        yield call(defineAdditionalAddressFieldsDisplay, {
            storeName,
            valuesSource: res,
            extractValuesWith: getPrefillValue,
        });
        const preventPrefillFieldsByForm = yield select(
            configSelectors.getPreventPrefillFieldsByForm,
            storeName,
        );

        yield all(
            registrationPrefillFields
                .filter(fieldName => !preventPrefillFieldsByForm.includes(fieldName))
                .map(fieldName => {
                    const fieldValue = getFieldValue(res, fieldName);
                    const preventPrefillFalsyValues = select(
                        configSelectors.getShouldPreventPrefillFalsyValues,
                    );
                    if (!preventPrefillFalsyValues || fieldValue) {
                        return put(actions.initDataFieldValue(fieldName, fieldValue));
                    }
                }),
        );
    }

    return canContinue;
}

const PHYSICAL_LOYALTY = 'PHYSICAL_LOYALTY';
const LOYALTY_OPTION_FIELD_NAME = 'loyaltyOptions';
const PIN_CODE_FIELD_NAME = 'pinCode';
const LOYALTY_DOB_FIELD_NAME = 'loyaltyDateOfBirth';

export function* beforeStepValidation({ selectors, actions }) {
    const shouldValidatePinOrDOB = yield select(configSelectors.shouldValidatePinOrDOB);
    if (shouldValidatePinOrDOB) {
        const loyaltyOption = yield select(selectors.getDataFieldValue, LOYALTY_OPTION_FIELD_NAME);
        const pinCode = yield select(selectors.getDataFieldValue, PIN_CODE_FIELD_NAME);
        const loyaltyDateOfBirth = yield select(
            selectors.getDataFieldValue,
            LOYALTY_DOB_FIELD_NAME,
        );
        const isBothNotEmpty = !!(pinCode && loyaltyDateOfBirth);
        const isValid =
            loyaltyOption !== PHYSICAL_LOYALTY ||
            !!(pinCode ? !loyaltyDateOfBirth : loyaltyDateOfBirth);
        yield put(
            actions.setIsPinCodeOrDOBValid({
                isPinCodeOrDOBValid: isValid,
                isPinCodeAndDOBFilled: isBothNotEmpty,
            }),
        );
        return isValid;
    }
    return true;
}

export function* onSetConstraintValid({ payload }) {
    const resetErrorForValidDOB = yield select(configSelectors.resetDOBErrorLoyaltyCardForm);
    if (!resetErrorForValidDOB || payload.name !== 'dateOfBirth') {
        return;
    }
    const { actions } = yield getContext('ctx');
    if (payload.isValid) yield put(actions.setDataFieldError(payload.name, ''));
}

export default (storeName, metadata) => formId => {
    const actions = createActions(storeName);
    const selectors = getSelectors(storeName);
    const types = getTypes(storeName);
    const ctx = { actions, selectors, types, storeName };

    return [
        ...dynaForms.createSagaFor(
            ctx,
            metadata || dynaFormsApi.getFormMetadata(formId || storeName),
            doSubmit,
            afterStepValidation,
            true,
            null,
            beforeStepValidation,
        ),
        watchPopUp(ctx),
        watchFormInitialize(ctx),
        watchSubmitLoyaltyCard(ctx),
        takeEvery([getTypes(storeName).AFTER_INITIALIZE, userTypes.SET_PROFILE], initialize, ctx),
        takeEvery(types.SET_IS_CONSTRAINT_VALID, onSetConstraintValid),
    ];
};
